// Whitespace (including non-breaking and narrow no-break spaces) and the
// zero-width marks that ride along when a link is copied out of an email or
// document. `\s` covers U+00A0 and U+202F but not the zero-width characters.
const INVISIBLE = "\\s\\u200B-\\u200D\\u2060\\uFEFF";
const EDGE_INVISIBLE = new RegExp(`^[${INVISIBLE}]+|[${INVISIBLE}]+$`, "g");

// Sentence punctuation that gets picked up when a link ends a sentence.
const TRAILING_JUNK = new RegExp(`[${INVISIBLE}.,;:!?)\\]}>'"]+$`);

// No portal route contains a backslash or a control character. Browsers
// normalise "/\\host" to "//host" (another origin), and strip tabs and newlines
// before navigating, so a path holding either is never repaired.
function isUnsafe(path: string): boolean {
	for (const char of path) {
		const code = char.charCodeAt(0);
		if (char === "\\" || code < 0x20 || code === 0x7f) {
			return true;
		}
	}
	return false;
}

function safeDecode(pathname: string): string {
	try {
		return decodeURI(pathname);
	} catch {
		return pathname;
	}
}

/**
 * Repairs the copy and paste mistakes that turn a valid investor portal link
 * into a 404, e.g. `/documents%C2%A0` (a trailing non-breaking space),
 * `/Documents/`, or `/documents.` at the end of a sentence.
 *
 * Returns the repaired path, or `null` when there is nothing to repair, it
 * repairs to the root, or it holds a backslash or control character (which
 * could repair into an off-origin URL), so the caller can fall back to the
 * default landing page. The repair is idempotent: a repaired path that still 404s returns
 * `null` on the next pass, so a redirect loop is not possible.
 *
 * Only the first segment is lowercased. Top-level routes are all lowercase,
 * but dynamic segments such as offer slugs may not be. A segment that still
 * holds a percent escape (malformed, so it could not be decoded) is left as
 * is, since lowercasing the escape would not make it match a route.
 */
export function cleanBrokenPath(pathname: string): string | null {
	const original = safeDecode(pathname);

	if (isUnsafe(original)) {
		return null;
	}

	const segments = original
		.split("/")
		.map((segment) => segment.replace(EDGE_INVISIBLE, ""))
		.filter(Boolean);

	while (segments.length > 0) {
		const last = segments[segments.length - 1].replace(TRAILING_JUNK, "");
		if (last) {
			segments[segments.length - 1] = last;
			break;
		}
		segments.pop();
	}

	if (segments.length === 0) {
		return null;
	}

	if (!segments[0].includes("%")) {
		segments[0] = segments[0].toLowerCase();
	}

	const cleaned = `/${segments.join("/")}`;
	return cleaned === original ? null : cleaned;
}
